import React from "react";
import { useQuery, useMutation } from "./urqlHooks";

const EERR = `mutation EERR($input: EERRInput!, $companyId: String!) {
    getEERRByYear(input: $input, companyId: $companyId) {
        companyId,
        isExempt,
        report {
        SALE {
            field
            fieldKey
            category
            categoryKey
            rank
            values {
              fieldValue
              period
              documents
            }
            costCenter {
              costCenterId
              costCentersValues {
                fieldValue
                period
                documents
              }
            }
          },
           COST{
            field
            fieldKey
            category
            categoryKey
            rank
            values {
              fieldValue
              period
              documents
            }
            costCenter {
              costCenterId
              costCentersValues {
                fieldValue
                period
                documents
              }
            }
          },
           GRASS_PROFIT{
            field
            fieldKey
            category
            categoryKey
            rank
            values {
              fieldValue
              period
            }
            costCenter {
              costCenterId
              costCentersValues {
                fieldValue
                period
                documents
              }
            }
          },
           EXPENSE{
            field
            fieldKey
            category
            categoryKey
            rank
            values {
              fieldValue
              period
              documents
            }
            costCenter {
              costCenterId
              costCentersValues {
                fieldValue
                period
                documents
              }
            }
          },
           OPERATING_PROFIT{
            field
            fieldKey
            category
            categoryKey
            rank
            values {
              fieldValue
              period
            }
           costCenter {
              costCenterId
              costCentersValues {
                fieldValue
                period
                documents
              }
            }
          },
           FINANCE{
            field
            fieldKey
            category
            categoryKey
            rank
            values {
              fieldValue
              period
              documents
            }
            costCenter {
              costCenterId
              costCentersValues {
                fieldValue
                period
                documents
              }
            }
          },
           OTHER_EXPENSE{
             field
             fieldKey
             category
             categoryKey
             rank
             values {
               fieldValue
               period
               documents
             }
            costCenter {
              costCenterId
              costCentersValues {
                fieldValue
                period
                documents
              }
            }
          },
           NET_PROFIT{
            field
            fieldKey
            category
            categoryKey
            rank
            values {
              fieldValue
              period
            }
            costCenter {
              costCenterId
              costCentersValues {
                fieldValue
                period
                documents
              }
            }
          },
        },
      margins {
        GRASS_PROFIT { 
          field
          fieldKey
          saleAreaFieldKey
          costAreaFieldKey
          values {
              field
              fieldKey
              category
              categoryKey
              rank
              values {
                fieldValue
                period
              }
          }
        }
      }   
    }
}`;

export const GetEERR = (variables) => {
  const companyId = variables?.input?.companyId;
  const [result, executeMutation] = useMutation(EERR);
  const { data, fetching, error } = result;
  
  const variablesKey = React.useMemo(() => {
    if (!variables?.input) return null;
    return JSON.stringify({
      companyId: variables.input.companyId,
      year: variables.input.year,
      showIVA: variables.input.showIVA,
      costCenterIds: variables.input.costCenterIds
    });
  }, [variables?.input?.companyId, variables?.input?.year, variables?.input?.showIVA, variables?.input?.costCenterIds]);
  
  const lastKeyRef = React.useRef(null);
  
  // Wrapper para reexecuteQuery que ejecuta la mutation
  const reexecuteQuery = React.useCallback((options) => {
    const vars = options?.variables || variables;
    if (vars?.input?.companyId) {
      return executeMutation(vars);
    }
    return Promise.resolve({ data: null, error: null });
  }, [variables, executeMutation]);
  
  // Ejecutar automáticamente cuando cambien las variables y haya companyId
  React.useEffect(() => {
    if (companyId && variables && variablesKey && variablesKey !== lastKeyRef.current) {
      lastKeyRef.current = variablesKey;
      executeMutation(variables);
    }
  }, [companyId, variablesKey, variables, executeMutation]);
  
  return { 
    data: data?.getEERRByYear ? { getEERRByYear: data.getEERRByYear } : data, 
    fetching, 
    error, 
    reexecuteQuery 
  };
};

const YEARS = `mutation YEARS($companyId: String!) {
  getEERRPeriods(companyId: $companyId)
}`;

export const GetYears = (variables) => {
  const companyId = variables?.companyId;
  const [result, executeMutation] = useMutation(YEARS);
  const { data, fetching, error } = result;
  
  const lastCompanyIdRef = React.useRef(null);
  
  // Wrapper para reexecuteQuery que ejecuta la mutation
  const reexecuteQuery = React.useCallback((options) => {
    const vars = options?.variables || variables;
    if (vars?.companyId) {
      return executeMutation(vars);
    }
    return Promise.resolve({ data: null, error: null });
  }, [variables, executeMutation]);
  
  // Ejecutar automáticamente cuando haya companyId
  React.useEffect(() => {
    if (companyId && companyId !== lastCompanyIdRef.current) {
      lastCompanyIdRef.current = companyId;
      executeMutation(variables);
    }
  }, [companyId, variables, executeMutation]);
  
  return { 
    data: data?.getEERRPeriods ? { getEERRPeriods: data.getEERRPeriods } : data, 
    fetching, 
    error, 
    reexecuteQuery 
  };
};

const EERR_STRUCTURE = `query GET_COMPANY_EERR_STRUCTURE($companyId: String!) {
  getCompanyEERRStructure(companyId: $companyId) {
    companyId,
    isExempt,
    report {
    SALE {
        field
        fieldKey
        category
        categoryKey
        rank
        values {
          fieldValue
          period
        }
      },
       COST{
        field
        fieldKey
        category
        categoryKey
        rank
        values {
          fieldValue
          period
        }
      },
           EXPENSE{
            field
            fieldKey
            category
            categoryKey
            rank
            values {
              fieldValue
              period
            }
      },
           FINANCE{
            field
            fieldKey
            category
            categoryKey
            rank
            values {
              fieldValue
              period
            }
      },
          OTHER_EXPENSE{
            field
            fieldKey
            category
            categoryKey
            rank
            values {
              fieldValue
              period
            }
      },
  }
}
}`;

export const GetEERRStructure = (variables) => {
  const [result, reexecuteQuery] = useQuery({
    query: EERR_STRUCTURE,
    variables,
  });

  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const UPDATE_EERR = `mutation UPDATE_EERR($companyId: String!, $isExempt: Boolean!) {
  updateEERR(companyId: $companyId, isExempt: $isExempt) {
    companyId,
    isExempt,
    report {
    SALE {
        field
        fieldKey
        category
        categoryKey
        rank
        values {
          fieldValue
          period
        }
      },
       COST{
        field
        fieldKey
        category
        categoryKey
        rank
        values {
          fieldValue
          period
        }
      },
           EXPENSE{
            field
            fieldKey
            category
            categoryKey
            rank
            values {
              fieldValue
              period
            }
      },
           FINANCE{
            field
            fieldKey
            category
            categoryKey
            rank
            values {
              fieldValue
              period
            }
      },
          OTHER_EXPENSE{
            field
            fieldKey
            category
            categoryKey
            rank
            values {
              fieldValue
              period
            }
      },
  }
}
}`;

export const UpdateEERR = () => {
  const [result, executeMutation] = useMutation(UPDATE_EERR);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const UPDATE_DOCUMENT_IN_EERR = `mutation UPDATE_DOCUMENT_IN_EERR($input: UpdateEERRDocument!, $companyId: String!) {
    updateDocumentInEERR(input: $input, companyId: $companyId) {
      status,
      message
    }
}`;

export const UpdateDocumentInEERR = () => {
  const [result, executeMutation] = useMutation(UPDATE_DOCUMENT_IN_EERR);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const CREATE_CATEGORY_IN_EERR = `mutation CREATE_CATEGORY_IN_EERR(
  $companyId: String!,
  $category: String!,
  $type: EERR_STRUCTURE!,
  $areas: [CreateCategoryAreasArray!],
  $field: String,
) {
  createNewCategory(
    companyId: $companyId,
    category: $category,
    type: $type,
    areas: $areas,
    field: $field,
  ) {
    status,
    message,
  }
}`;

export const CreateCategoryInEERR = () => {
  const [result, executeMutation] = useMutation(CREATE_CATEGORY_IN_EERR);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const CREATE_AREA_IN_EERR = `mutation CREATE_AREA_IN_EERR(
  $companyId: String!,
  $category: String!,
  $categoryKey: String!,
  $type: EERR_STRUCTURE!,
  $field: String!,
) {
  createNewArea(
    companyId: $companyId,
    category: $category,
    categoryKey: $categoryKey,
    type: $type,
    field: $field,
  ) {
    status,
    message,
  }
}`;

export const CreateAreainEERR = () => {
  const [result, executeMutation] = useMutation(CREATE_AREA_IN_EERR);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};


const EDIT_AREA_IN_EERR = `mutation EDIT_AREA_IN_EERR(
  $companyId: String!,
  $fieldKey: String!,
  $newField: String!,
  $type: EERR_STRUCTURE!,
) {
  editEERRArea(
    companyId: $companyId,
    fieldKey: $fieldKey,
    newField: $newField,
    type: $type,
  ) {
    status,
    message,
  }
}`;

export const EditAreainEERR = () => {
  const [result, executeMutation] = useMutation(EDIT_AREA_IN_EERR);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const EDIT_CATEGORY_IN_EERR = `mutation EDIT_CATEGORY_IN_EERR(
  $companyId: String!,
  $newCategory: String!,
  $categoryKey: String!,
  $type: EERR_STRUCTURE!,
  $areas: [CreateCategoryAreasArray!],
) {
  editEERRCategory(
    companyId: $companyId,
    newCategory: $newCategory,
    categoryKey: $categoryKey,
    type: $type,
    areas: $areas,
  ) {
    status,
    message,
  }
}`;

export const EditCategoryinEERR = () => {
  const [result, executeMutation] = useMutation(EDIT_CATEGORY_IN_EERR);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const DELETE_AREA_IN_EERR = `mutation DELETE_AREA_IN_EERR(
  $companyId: String!,
  $fieldKey: String!,
  $type: EERR_STRUCTURE!,
) {
  deleteEERRArea(
    companyId: $companyId,
    fieldKey: $fieldKey,
    type: $type,
  ) {
    status,
    message,
  }
}`;

export const DeleteAreainEERR = () => {
  const [result, executeMutation] = useMutation(DELETE_AREA_IN_EERR);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};
